import { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { AppLayout } from './components/layout/AppLayout'
import { PageLoader } from './components/ui/LoadingSpinner'
import useAuthStore from './store/authStore'

// Public pages
import { LandingPage } from './pages/anonymous/LandingPage'
import { AnonUpload } from './pages/anonymous/AnonUpload'
import { AnonResults } from './pages/anonymous/AnonResults'
import { PrivacyPolicyPage, TermsPolicyPage, CookiesPolicyPage } from './pages/legal/LegalPolicyPage'

// Auth pages
import { SignIn } from './pages/auth/SignIn'
import { SignUp } from './pages/auth/SignUp'
import { ConfirmEmail } from './pages/auth/ConfirmEmail'
import { ForgotPassword } from './pages/auth/ForgotPassword'

// App pages
import { Dashboard } from './pages/dashboard/Dashboard'
import { NewJob } from './pages/dashboard/NewJob'
import { JobDetail } from './pages/dashboard/JobDetail'
import { JobComparison } from './pages/dashboard/JobComparison'
import { Settings } from './pages/settings/Settings'
import { BillingSuccess } from './pages/settings/BillingSuccess'
import { CancelSubscription } from './pages/settings/CancelSubscription'
import { AdminPanel } from './pages/admin/AdminPanel'
import { HelpPage } from './pages/dashboard/HelpPage'

function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuthStore()
  const location = useLocation()
  if (isLoading) return <PageLoader />
  if (!isAuthenticated) {
    const redirect = encodeURIComponent(`${location.pathname}${location.search}`)
    return <Navigate to={`/signin?redirect=${redirect}`} replace />
  }
  return children
}

function PublicRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuthStore()
  if (isLoading) return <PageLoader />
  if (isAuthenticated) return <Navigate to="/dashboard" replace />
  return children
}

function AdminRoute({ children }) {
  const { user, isLoading } = useAuthStore()
  if (isLoading) return <PageLoader />
  if (!user?.isPlatformAdmin) return <Navigate to="/dashboard" replace />
  return children
}

// Handles ?verified=true on signin page
function SignInWithVerified() {
  const [searchParams] = useSearchParams()
  const verified = searchParams.get('verified')
  const email = searchParams.get('email') || ''
  const redirect = searchParams.get('redirect') || '/dashboard'
  return <SignIn verified={verified === 'true'} initialEmail={email} redirectTo={redirect} />
}

export default function App() {
  const { init, isLoading } = useAuthStore()

  useEffect(() => {
    init()
  }, [])

  if (isLoading) return <PageLoader />

  return (
    <Routes>
      {/* Landing */}
      <Route path="/" element={
        <PublicRoute>
          <LandingPage />
        </PublicRoute>
      } />

      {/* Anonymous flow */}
      <Route path="/upload" element={<AnonUpload />} />
      <Route path="/results" element={<AnonResults />} />

      {/* Legal policies */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPolicyPage />} />
      <Route path="/cookies" element={<CookiesPolicyPage />} />

      {/* Auth pages */}
      <Route path="/signin" element={
        <PublicRoute>
          <SignInWithVerified />
        </PublicRoute>
      } />
      <Route path="/signup" element={
        <PublicRoute>
          <SignUp />
        </PublicRoute>
      } />
      <Route path="/confirm-email" element={<ConfirmEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/accept-invite" element={<Navigate to="/signin" replace />} />

      {/* Protected app routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="jobs/new" element={<NewJob />} />
        <Route path="jobs/:jobId" element={<JobDetail />} />
        <Route path="jobs/:jobId/compare" element={<JobComparison />} />
        <Route path="jobs/:jobId/candidates/:candidateId" element={<JobDetail />} />
        <Route path="team" element={<Navigate to="/dashboard" replace />} />
        <Route path="settings" element={<Settings />} />
        <Route path="billing/success" element={<BillingSuccess />} />
        <Route path="billing/cancel" element={<CancelSubscription />} />
        <Route path="admin" element={
          <AdminRoute>
            <AdminPanel />
          </AdminRoute>
        } />
        <Route path="help" element={<HelpPage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
